import { useState } from 'react';
import { Badge, Button, ConfirmModal, Input } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import {
  useRenameCredential,
  useRevokeCredential,
  useSetDefaultCredential,
  useStartOAuth,
  useVerifyCredential,
} from '@/hooks/useIdentityCredentials';
import {
  credentialStatusLabel,
  credentialStatusVariant,
  formatExpiresIn,
  isDeviceStart,
  openIdentityOAuthUrl,
} from './identity-lib';
import CredentialActionsMenu, { type CredentialAction } from './CredentialActionsMenu';
import DeviceAuthModal from './DeviceAuthModal';
import TestAwsRoleModal from './TestAwsRoleModal';
import type { Credential, OAuthDeviceStart } from '@/types/identity';
import type { Scope } from './scope';

interface Props {
  credential: Credential;
  canManage: boolean;
  hasSiblings: boolean;
  scope: Scope;
}

export default function AccountRow({
  credential,
  canManage,
  hasSiblings,
  scope,
}: Props) {
  const { success, error: showError } = useToast();
  const verify = useVerifyCredential();
  const revoke = useRevokeCredential();
  const setDefault = useSetDefaultCredential();
  const rename = useRenameCredential();
  const startOAuth = useStartOAuth();

  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(
    credential.metadata.accountName ?? '',
  );
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [deviceStart, setDeviceStart] = useState<OAuthDeviceStart | null>(null);
  const [testAsAgent, setTestAsAgent] = useState(false);

  const accountName =
    credential.metadata.accountName ??
    credential.displayName ??
    credential.metadata.displayName ??
    credential.metadata.externalUsername ??
    'Untitled account';
  const isAwsRole = credential.authType === 'aws_role';
  const subtitle = isAwsRole
    ? // IAM roles: show which role the label stands for.
      `${String(credential.metadata.roleName ?? '')} · ${String(credential.metadata.accountId ?? '')}`
    : credential.metadata.externalUsername &&
        credential.metadata.externalUsername !== accountName
      ? credential.metadata.externalUsername
      : undefined;
  const lastVerifyError =
    isAwsRole && credential.status === 'error' && typeof credential.metadata.lastVerifyError === 'string'
      ? credential.metadata.lastVerifyError
      : undefined;
  const now = Date.now();
  // Auto-refreshing credentials (AWS SSO device-login sessions, and any other
  // provider the backend rotates server-side) expose `nextRefreshAt`: when the
  // scheduler re-mints the token, ~10 min before `expiresAt`. Its mere presence
  // is the signal — the backend only attaches it to the renewable session row,
  // never to derived/short-lived rows (e.g. aws_temp role creds). Treat it as
  // optional: rows minted before the deploy, or without a refresh token, won't
  // carry it and fall back to the plain expiry chip below.
  const refreshesIn =
    credential.nextRefreshAt && credential.status === 'connected'
      ? formatExpiresIn(credential.nextRefreshAt - now)
      : null;
  // Validity window — time until the (short-lived) access token expires.
  const validFor = credential.expiresAt
    ? formatExpiresIn(credential.expiresAt - now)
    : null;
  // Terminal "expires in" chip — unchanged behavior for non-auto-refreshing
  // oauth2 creds; suppressed when a refresh is scheduled (otherwise a
  // self-renewing credential reads as a scary one-shot countdown).
  const expiresIn =
    !refreshesIn && credential.authType === 'oauth2' ? validFor : null;

  const handleAction = async (action: CredentialAction) => {
    switch (action) {
      case 'verify': {
        try {
          const result = await verify.mutateAsync(credential.credId);
          if (result.ok) {
            const who = result.account.externalUsername ?? result.account.displayName;
            success(who ? `Connection successful — ${who}` : 'Connection successful');
          } else {
            // Failure-as-data — keep the messaging soft.
            showError(`Cannot test: ${result.message ?? result.reason}`);
          }
        } catch (e) {
          showError(e instanceof Error ? e.message : 'Verify failed');
        }
        return;
      }
      case 'test-as-agent':
        setTestAsAgent(true);
        return;
      case 'set-default':
        try {
          await setDefault.mutateAsync(credential.credId);
          success('Default updated');
        } catch (e) {
          showError(e instanceof Error ? e.message : 'Set-default failed');
        }
        return;
      case 'rename':
        setRenameValue(credential.metadata.accountName ?? '');
        setRenaming(true);
        return;
      case 'reconnect':
        if (credential.authType !== 'oauth2') return;
        // Reconnect is OAuth-only and OAuth start is space-only. Org-scope
        // credentials are API-key only, so this branch is unreachable from
        // the org tab.
        if (scope.kind !== 'space') return;
        try {
          const result = await startOAuth.mutateAsync({
            scope: { spaceId: scope.spaceId },
            provider: credential.provider,
            body: {
              redirectUri: `${window.location.origin}/oauth/identity-callback`,
              returnUrl: `${window.location.origin}/oauth/connected`,
              providerInstanceId: credential.providerInstanceId,
            },
          });
          // Device-authorization providers (aws-sso) re-auth through the
          // device modal rather than a redirect URL.
          if (isDeviceStart(result)) {
            setDeviceStart(result);
          } else {
            openIdentityOAuthUrl(result.authorizationUrl, {
              provider: credential.provider,
              credId: credential.credId,
              scope: scope.kind,
            });
          }
        } catch (e) {
          showError(e instanceof Error ? e.message : 'Reconnect failed');
        }
        return;
      case 'revoke':
        setConfirmRevoke(true);
        return;
    }
  };

  const onRenameSubmit = async () => {
    const next = renameValue.trim();
    if (!next || next === credential.metadata.accountName) {
      setRenaming(false);
      return;
    }
    try {
      await rename.mutateAsync({ credId: credential.credId, accountName: next });
      success('Renamed');
      setRenaming(false);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Rename failed');
    }
  };

  const onConfirmRevoke = async () => {
    try {
      await revoke.mutateAsync(credential.credId);
      success('Credential revoked');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Revoke failed');
    } finally {
      setConfirmRevoke(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors">
        <div className="flex-1 min-w-0">
          {renaming ? (
            <div className="flex items-center gap-2">
              <Input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onRenameSubmit();
                  if (e.key === 'Escape') setRenaming(false);
                }}
                className="h-7 max-w-xs"
                placeholder="e.g. Personal, Sales Inbox"
              />
              <Button size="sm" onClick={onRenameSubmit} isLoading={rename.isPending}>
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRenaming(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-foreground truncate">
                {accountName}
              </span>
              {subtitle && (
                <span className="text-xs text-muted-foreground truncate">
                  {subtitle}
                </span>
              )}
              {credential.metadata.isDefault && (
                <Badge variant="info">Default</Badge>
              )}
              <Badge variant={credentialStatusVariant(credential.status)}>
                {credentialStatusLabel(credential.status)}
              </Badge>
              {refreshesIn !== null && (
                <span className="text-xs text-muted-foreground">
                  {validFor ? `Valid ${validFor} · ` : ''}auto-refreshes in{' '}
                  {refreshesIn}
                </span>
              )}
              {expiresIn !== null && credential.status === 'connected' && (
                <span className="text-xs text-muted-foreground">
                  expires in {expiresIn}
                </span>
              )}
              {lastVerifyError && (
                <span className="basis-full text-xs text-status-error-fg break-words">
                  {lastVerifyError}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <CredentialActionsMenu
            credential={credential}
            canManage={canManage}
            hasSiblings={hasSiblings}
            onAction={handleAction}
          />
        </div>
      </div>
      <ConfirmModal
        isOpen={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        onConfirm={onConfirmRevoke}
        title="Revoke Credential?"
        message={`${accountName} will be revoked. Any agent or tool relying on this credential will lose access immediately.`}
        confirmText="Revoke"
        variant="destructive"
        isLoading={revoke.isPending}
      />
      {deviceStart && (
        <DeviceAuthModal
          isOpen={true}
          onClose={() => setDeviceStart(null)}
          provider={{
            id: credential.provider,
            displayName: credential.displayName ?? credential.provider,
          }}
          device={deviceStart}
        />
      )}
      {testAsAgent && (
        <TestAwsRoleModal
          isOpen={true}
          onClose={() => setTestAsAgent(false)}
          credential={credential}
        />
      )}
    </>
  );
}
